/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { colors, colorUtils } from '../../../styles/colorPalette';

const CommonButton = ({
    children,
    variant = 'default', // 'default', 'outlined', 'text', 'image', 'primary', 'secondary', 'accent', 'navigation'
    size = 'medium', // 'small', 'medium', 'large'
    disabled = false,
    fullWidth = false,
    startIcon,
    endIcon,
    imageSrc,
    imageAlt,
    hoverImageSrc,
    layout = 'horizontal', // 'horizontal', 'vertical'
    navigationType, // 'gallery', 'map', 'lab1', 'lab2', 'address'
    onClick,
    onMouseEnter,
    onMouseLeave,
    customStyles,
    className,
    ...props
}) => {
    const baseStyles = css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-direction: ${layout === 'vertical' ? 'column' : 'row'};
        border: none;
        border-radius: 4px;
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        font-family: inherit;
        font-weight: 500;
        text-decoration: none;
        transition: all 0.2s ease-in-out;
        outline: none;
        position: relative;
        width: ${fullWidth ? '100%' : 'auto'};

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    `;

    const variantStyles = {
        default: css`
            background-color: ${colors.primary.main} !important;
            color: ${colors.primary.contrast} !important;
            &:hover:not(:disabled) {
                background-color: ${colors.primary.light} !important;
            }
        `,
        outlined: css`
            background-color: transparent !important;
            color: ${colors.primary.main} !important;
            border: 1px solid ${colors.primary.main} !important;
            &:hover:not(:disabled) {
                background-color: ${colors.primary.main} !important;
                color: ${colors.primary.contrast} !important;
            }
        `,
        text: css`
            background-color: transparent !important;
            color: ${colors.primary.main} !important;
            &:hover:not(:disabled) {
                background-color: ${colorUtils.withAlpha(colors.primary.main, 0.1)} !important;
            }
        `,
        image: css`
            background-color: transparent !important;
            padding: 0;
            &:hover:not(:disabled) {
                transform: scale(1.05);
            }
        `,
        secondary: css`
            background-color: ${colors.secondary.main} !important;
            color: ${colors.secondary.contrast} !important;
            &:hover:not(:disabled) {
                background-color: ${colors.secondary.light} !important;
            }
        `,
        accent: css`
            background-color: ${colors.accent.main} !important;
            color: ${colors.accent.contrast} !important;
            &:hover:not(:disabled) {
                background-color: ${colors.accent.light} !important;
            }
        `,
        neutral: css`
            background-color: ${colors.neutral.main} !important;
            color: ${colors.neutral.contrast} !important;
            &:hover:not(:disabled) {
                background-color: ${colors.neutral.light} !important;
            }
        `,
        navigation: css`
            background-color: ${navigationType && colors.navigation[navigationType]
                ? colors.navigation[navigationType].main
                : colors.primary.main} !important;
            color: ${navigationType && colors.navigation[navigationType]
                ? colors.navigation[navigationType].contrast
                : colors.primary.contrast} !important;
            &:hover:not(:disabled) {
                background-color: ${navigationType && colors.navigation[navigationType]
                    ? colors.navigation[navigationType].dark
                    : colors.primary.dark} !important;
            }
        `,
    };
    const sizeStyles = {
        small: css`
            padding: 6px 12px;
            font-size: 12px;
            min-height: 32px;
        `,
        medium: css`
            padding: 8px 16px;
            font-size: 14px;
            min-height: 40px;
        `,
        large: css`
            padding: 12px 24px;
            font-size: 16px;
            min-height: 48px;
        `,
        startTheMap: css`
            font-size: 16px;
            min-height: 48px;
        `,
        icon: css`
            padding: 8px;
            min-width: 40px;
            min-height: 40px;
        `,
    };

    const [currentImageSrc, setCurrentImageSrc] = React.useState(imageSrc);

    const handleMouseEnter = (e) => {
        if (hoverImageSrc && variant === 'image') {
            setCurrentImageSrc(hoverImageSrc);
        }
        onMouseEnter?.(e);
    };

    const handleMouseLeave = (e) => {
        if (hoverImageSrc && variant === 'image') {
            setCurrentImageSrc(imageSrc);
        }
        onMouseLeave?.(e);
    };

    const renderContent = () => {
        if (variant === 'image' && imageSrc) {
            return (
                <img
                    src={currentImageSrc}
                    alt={imageAlt || 'button image'}
                    css={css`
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                    `}
                />
            );
        }

        return (
            <>
                {startIcon && (
                    <span
                        css={css`
                            display: flex;
                            align-items: center;
                            ${layout === 'vertical' ? 'margin-bottom: 8px;' : 'margin-right: 8px;'}
                        `}
                    >
                        {startIcon}
                    </span>
                )}
                {children}
                {endIcon && (
                    <span
                        css={css`
                            display: flex;
                            align-items: center;
                            ${layout === 'vertical' ? 'margin-top: 8px;' : 'margin-left: 8px;'}
                        `}
                    >
                        {endIcon}
                    </span>
                )}
            </>
        );
    };

    return (
        <button
            css={[baseStyles, variantStyles[variant], sizeStyles[size], customStyles]}
            className={className}
            disabled={disabled}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...props}
        >
            {renderContent()}
        </button>
    );
};

export default CommonButton;
